import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Badge, Button, Input, PopoverBody, PopoverHeader, UncontrolledPopover, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom"; 
import { getAuthUser } from "../../../helpers/utils";
import PaginationControls from "./PaginationControls";
import "../../../App.css"; 
import { t } from "i18next";
import { useSelector } from "react-redux";
import Rating from "react-rating";

  // Consider moving StatusObj to a config file or pass via props if it varies
  const StatusLookups = {
    1: {
      text: { en: "Pending", ar: "قيد الانتظار" },
      color: "warning",
    },
    2: {
      text: { en: "OnGoing", ar: "جاري التنفيذ" },
      color: "info",
    },
    3: {
      text: { en: "Completed", ar: "مكتمل" },
      color: "success",
    },
    4: {
      text: { en: "Cancelled", ar: "ملغى" },
      color: "danger",
    },
  }
 
  const paymentStatusOptions =  [
    { value: "Pending", label: t("Pending") },
    { value: "Completed", label: t("Completed") },
    { value: "Failed", label: t("Failed") },
  ];

  // Helper to format date as DD/MM/YYYY
  const formatDateToDDMMYYYY = (dateInstance) => {
    // Ensure dateInstance is a valid Date object before calling methods
    const day = String(dateInstance.getDate()).padStart(2, '0');
    const month = String(dateInstance.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const year = dateInstance.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Helper for cell rendering 
  const renderCellContent = (item, col, t, i18n, handlers, editState, users, referralStatus, disabled) => {
    const { user } = getAuthUser(); 
    const value = item[col.Title]; // Defined once at the top

    const {
      onActiveOrDeactiveChange,
      handleCheckbox,
      handleButtonClick,
      onInlineUpdate,
      permission,
      onViewHistory,
      onRowClick
    } = handlers;
    const { editingCell, editedValue, setEditingCell, setEditedValue } = editState;
    
    switch (col.FieldName) {
      case "Translate": {
        let textContent;
        if (typeof value === "object" && value !== null) {
          textContent = value[i18n.language];
        } else {
          textContent = value;
        }
        const displayValue = (textContent === null || textContent === undefined || String(textContent).trim() === "") ? "-" : textContent;
        return (
          <div className="wrap-text">
            {t(displayValue)}
          </div>
        );
      }

      case "Normal": {
        let textContent;
        if (typeof value === "object" && value !== null) {
          textContent = value[i18n.language];
        } else {
          textContent = value;
        }
        const displayValue = (textContent === null || textContent === undefined || String(textContent).trim() === "") ? "-" : textContent;
        return (
          <div className="wrap-text">
            {displayValue}
          </div>
        );
      }

      case "Normal-Cursor": {
        let textContent;
        if (typeof value === "object" && value !== null) {
          textContent = value[i18n.language];
        } else {
          textContent = value;
        }
        const displayValue = (textContent === null || textContent === undefined || String(textContent).trim() === "") ? "-" : textContent;
        return (
          <div className="wrap-text cursor-pointer" onClick={() => onRowClick?.(item, col.Title)}>
            {displayValue}
          </div>
        );
      }

      case "Normal-Color-Cursor": {
        let textContent;
        if (typeof value === "object" && value !== null) {
          textContent = value[i18n.language];
        } else {
          textContent = value;
        }
        const displayValue = (textContent === null || textContent === undefined || String(textContent).trim() === "") ? "-" : textContent;
        return (
          <div className={`wrap-text cursor-pointer ${item.JsonFieldUpdated?.includes(col.Title) ? 'text-success' : 'text-danger'}`} onClick={() => onRowClick?.(item, col.Title)}>
            {displayValue}
          </div>
        );
      }

      case "Label":
        const statusDefinition = StatusLookups[value]; 
        
        let displayText;
        let badgeColor;

        if (statusDefinition) {
          displayText = statusDefinition.text[i18n.language] || statusDefinition.text['en']; 
          badgeColor = statusDefinition.color;
        } else {
          displayText = (value !== null && value !== undefined) ? String(value) : "-";
          badgeColor = 'secondary'; 
        }

        return (
        <>
          <span className={`badge fs-13 bg-${badgeColor}-subtle text-${badgeColor}`}>
            {displayText}
          </span> 
        </>
        );

      case "BooleanLabel": {
        const isTrue = value === true;
        const text = isTrue ? t("Yes") : t("No");
        const color = isTrue ? "success" : "danger";
        if (value === null || value === undefined) {
          return <div className="wrap-text">-</div>;
        }
        return (
          <span className={`badge fs-13 bg-${color}-subtle text-${color}`}>
            {text}
          </span>
        );
      }

      case "ColorPickerLabel": {
        const colorCode = value;
        const displayValue = (colorCode === null || colorCode === undefined || String(colorCode).trim() === "") ? "-" : colorCode;
        return (
          <>
            {displayValue !== "-" ? (
               <span className={`badge fs-13 bg-${colorCode}-subtle `} style={{backgroundColor: colorCode}}>
                  {displayValue}
               </span> 
              ) : (
                <span>{displayValue}</span>
              )
            }
        </>
        );
      }

      case "Button":
      case "Button2": 
      case "ButtonTxt":
        // If the value is null, display "-" as requested.
        if (value === "") {
          return <div className="wrap-text">-</div>;
        }
        // If no click handler is provided, display a muted "-"
        if (!handleButtonClick) return <span className="text-muted">-</span>;
        
        let buttonText = value;
        if (col.FieldName === "ButtonTxt") {
          buttonText = value ? t("View") : t("Add");
        } else if (col.FieldName === "Button" && value == 0) { 
          buttonText = t("Add");
        }
        return (
          <Button
              type="button"
              color="soft-secondary" // Using reactstrap color prop
              size="sm"
              onClick={() => handleButtonClick(item, col.Title)}
          >
              {buttonText}
          </Button>
        );

      case "ProcessedBtn": 
        // If no click handler is provided, display a muted "-"
        if (!handleButtonClick) return <span className="text-muted">-</span>;
        const { FileURL, IsProcessed, IsSaved, IsLargeFile, LargeDocumentFiles, DocumentTypeID, ReviewedByID, Status } = item;
        const hasJson = value == 'null' ? false : Boolean(value);
        const isProcessing = IsLargeFile && item.LargeDocumentFiles
          ? item.LargeDocumentFiles?.some(f => !f.JsonURL && !f.IsError)
          : false;

        // const isError = IsLargeFile && item.LargeDocumentFiles
        //   ? item.LargeDocumentFiles?.some(f => f.IsError)
        //   : false;

        // Single button conditions
        if (!FileURL || (IsLargeFile && !hasJson && !LargeDocumentFiles)) {
          return (
            <Button type="button" color="danger" size="sm" disabled>
              {t("No file")}
            </Button>
          );
        }

        if (IsProcessed == false || isProcessing || Status == 10) {
          return (
            <Button type="button" color="danger" size="sm" disabled>
              <Spinner size="sm" className="me-2 ms-2" />
              {Status == 10 ? t("Retry...") : t("Processing...")}
            </Button>
          );
        }

        if (!hasJson && !(IsProcessed == false) && [1].includes(user.RoleID)) {
          return (
            <Button
              type="button"
              color="secondary"
              size="sm"
              disabled={disabled}
              onClick={() => handleButtonClick(item, col.Title, "get-json")}
            >
              {t("Get JSON")}
            </Button>
          );
        }

        // Double button (or single View)
        if (hasJson && IsProcessed == true) {
          return (
            <div className="d-flex justify-content-center align-items-center gap-2">
              <Button
                type="button"
                color="primary"
                size="sm"
                onClick={() => handleButtonClick(item, col.Title, "view")}
              >
                {t("View")}
              </Button>

              {(DocumentTypeID == 4 && user.RoleID == 1) && (
                <> { Status == 4 && ReviewedByID &&
                    <Button
                      type="button"
                      color="primary"
                      size="sm"
                      disabled={disabled}
                      onClick={() => handleButtonClick(item, col.Title, "view-metadata")}
                    >
                      {t("MetaData")}
                    </Button>
                  }
                  <Button
                    type="button"
                    color="primary"
                    size="sm"
                    disabled={disabled}
                    onClick={() => handleButtonClick(item, col.Title, "json-editor")}
                  >
                    {t("JSON")}
                  </Button>
                  { (item.Sections || item.ArticleRange) &&
                    <Button
                      type="button"
                      color="primary"
                      size="sm"
                      disabled={disabled}
                      onClick={() => handleButtonClick(item, col.Title, "configuration")}
                    >
                      {t("Configuration")}
                    </Button>
                  }
                </>
              )}

              {/* {(!IsSaved) && (
                <Button
                  type="button"
                  color="danger"
                  size="sm"
                  disabled={disabled}
                  onClick={() => handleButtonClick(item, col.Title, "retry")}
                >
                  {t("Retry")}
                </Button>
              )} */}
            </div>
          );
        }
        return <span className="text-muted">-</span>;

      case "ProcessedBtn2": 
        // If no click handler is provided, display a muted "-"
        if (!handleButtonClick) return <span className="text-muted">-</span>;

        if (!item.FileURL) {
          return (
            <Button type="button" color="danger" size="sm" disabled>
              {t("No file")}
            </Button>
          );
        }

        if (item.Status == 3 || item.Status == 10) {
          return (
            <Button type="button" color="danger" size="sm" disabled>
              <Spinner size="sm" className="me-2 ms-2" />
              {item.Status == 10 ? t("Retry...") : t("Processing...")}
            </Button>
          );
        }

        if (item.Status == 2 && [1].includes(user.RoleID)) {
          return (
            <Button
              type="button"
              color="secondary"
              size="sm"
              disabled={disabled}
              onClick={() => handleButtonClick(item, col.Title, "get-json")}
            >
              {t("Get JSON")}
            </Button>
          );
        }

        // Double button (or single View)
        if (item.Status > 2 && item.Status != 11) {
          return (
            <div className="d-flex justify-content-center align-items-center gap-2">
              <Button
                type="button"
                color="primary"
                size="sm"
                onClick={() => handleButtonClick(item, col.Title, "view")}
              >
                {t("View")}
              </Button>

              {user.RoleID == 1 && (
                <> { [6, 12, 13].includes(item.Status) &&
                    <Button
                      type="button"
                      color="primary"
                      size="sm"
                      disabled={disabled}
                      onClick={() => handleButtonClick(item, col.Title, "view-metadata")}
                    >
                      {t("MetaData")}
                    </Button>
                  }
                  <Button
                    type="button"
                    color="primary"
                    size="sm"
                    disabled={disabled}
                    onClick={() => handleButtonClick(item, col.Title, "json-editor")}
                  >
                    {t("JSON")}
                  </Button>
                </>
              )}
            </div>
          );
        }
        return <span className="text-muted">-</span>;
        
      case "Active":
        return (
          <div className="d-flex justify-content-center form-check form-switch">
            <Input // Changed from <input> to <Input> for Reactstrap
              className="form-check-input"
              type="checkbox"
              role="switch"
              id={`switch-${item.Id}-${col.Title}`} // More unique ID
              onChange={(e) => onActiveOrDeactiveChange?.(item, e)}
              checked={value === true}
              disabled={!permission?.CanActive}
            />
            <label
              className="form-check-label"
              htmlFor={`switch-${item.Id}-${col.Title}`}
            ></label>
          </div>
        );

      case "Checkbox":
        return (
          <div className="form-check" style={{ textAlign: "center", verticalAlign: "middle" }}>
            <Input // Changed from <input> to <Input> for Reactstrap
              type="checkbox"
              checked={!!value} // Ensure boolean
              onChange={(e) => handleCheckbox?.(e, { ...item }, col.Title)}
              id={`checkbox-${item.Id}-${col.Title}`} // More unique ID
            />
          </div>
        );

      case "Icon":
        const altText = i18n.language === 'ar' ? (col.ValueAr || col.Title) : (col.ValueEn || col.Title);
        return (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
            {value ? (
              <img
                src={value}
                alt={altText || "Icon"}
                className="rounded-circle avatar-xs"
                style={{ objectFit: 'cover' }}
                onError={(e) => { e.target.style.display = 'none'; /* Hide on error */ }}
              />
            ) : '-'}
          </div>
        );

      case "List":
        // Check if the data is a valid, non-empty array
        if (Array.isArray(item[col.Title]) && item[col.Title].length > 0) {
          const listData = item[col.Title];
          
          // Using item.ID is perfect for a unique ID
          const popoverId = `list_popover_${col.Title}_${item.ID}`;

          // A nice array of colors for our tags
          const tagColors = [
            "primary", "secondary", "success", "danger", 
            "warning", "info", "dark"
          ];

          return (
            // Use a React Fragment to return two sibling elements
            <>
              {/* --- ENHANCED UI: The Trigger Element --- */}
              {/* We use an anchor tag to get hover effects for free */}
              <a
                href="#!"
                onClick={(e) => e.preventDefault()}
                id={popoverId}
                className="d-inline-flex align-items-center" // Use flexbox for alignment
                style={{ textDecoration: 'none', gap: '0.3rem' }} // Remove underline, add space
              >
                {/* <i className="bx bxs-tag" style={{ color: "#6c757d" }} />  */}
                <Badge pill color="light" className="text-dark" >
                  {listData.length}
                </Badge>
              </a>

              {/* --- The Popover that appears on hover --- */}
              <UncontrolledPopover
                placement="auto"
                target={popoverId}
                trigger="hover"
                // Optional: Add a small delay before it hides
                delay={{ hide: 150 }} 
              >
                <PopoverHeader>{i18n.language === "ar" ? col.ValueAr : col.ValueEn}</PopoverHeader>
                <PopoverBody>
                  {/* --- ENHANCED UI: The Popover Content --- */}
                  {/* We use a flex container to display tags in a row */}
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap', // Allows tags to wrap to the next line
                    gap: '0.5rem'      // Creates space between tags
                  }}>
                    {listData.map((val, idx) => (
                      <Badge 
                        key={idx} 
                        color={tagColors[idx % tagColors.length]} // Cycle through our colors
                        pill // Gives the badge a rounded "pill" shape
                        style={{ fontSize: '0.8rem', fontWeight: '500' }}
                      >
                        {typeof val === "object" && val !== null
                          ? val[i18n.language] ?? "-"
                          : String(val)}
                      </Badge>
                    ))}
                  </div>
                </PopoverBody>
              </UncontrolledPopover>
            </>
          );

        } else {
          // If the array is empty or not an array, show a dash
          return "-"; 
      }

      case "List-Cursor":
        if (Array.isArray(item[col.Title]) && item[col.Title].length > 0) {
          return (
            <ul style={{marginTop: '1rem'}} className={`wrap-text cursor-pointer ${item.JsonFieldUpdated?.includes("InstrumentType") ? 'text-success' : 'text-danger'}`} onClick={() => onRowClick(item, col.Title)}>
							{item[col.Title]?.map((item, index) => (
								<li key={index}>{item}</li>
							))}
						</ul>
          )
        } else {
          return "-"; 
        }

      case "Time":
      case "DateTime": {
        if (!value) return "-";
        
        // Try native Date parse first
        let dateObj = new Date(value);
        
        // If native parse fails, try to handle time-only formats like
        // "12:22:00+00:00", "12:22:00+00", "12:22:00", or "12:00 AM"
        if (isNaN(dateObj.valueOf())) {
          const str = String(value).trim();
          
          // Match HH:MM(:SS)? with optional timezone offset (+00 or +00:00)
          const timeWithTz = str.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:([+\-]\d{2}(?::?\d{2})?))?$/);
          if (timeWithTz) {
            const hh = String(timeWithTz[1]).padStart(2, '0');
            const mm = timeWithTz[2];
            const ss = timeWithTz[3] || '00';
            const tz = timeWithTz[4] || '';
            // Build an ISO-like timestamp anchored to 1970-01-01 so Date will parse it
            // Normalize timezone offsets: '+00' -> '+00:00'
            let tzNormalized = tz || "";
            if (tzNormalized && !tzNormalized.includes(":")) {
              tzNormalized = tzNormalized + ":00";
            }
            const iso = `1970-01-01T${hh}:${mm}:${ss}${tzNormalized}`;
            dateObj = new Date(iso);
          } else {
            // Match 12-hour format like "12:00 AM" or "1:05 pm"
            const ampm = str.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
            if (ampm) {
              let h = parseInt(ampm[1], 10);
              const m = ampm[2];
              const meridian = ampm[3].toUpperCase();
              if (meridian === 'PM' && h < 12) h += 12;
              if (meridian === 'AM' && h === 12) h = 0;
              const hh = String(h).padStart(2, '0');
              dateObj = new Date(`1970-01-01T${hh}:${m}:00`);
            }
          }
        }

        if (isNaN(dateObj.valueOf())) return "-";

        const formattedTimePart = dateObj.toLocaleTimeString(i18n.language, { timeStyle: 'short' });

        // If original value contains a date component, show date + time
        const hasDate = /\d{4}-\d{2}-\d{2}/.test(String(value));
        if (col.FieldName === 'DateTime' || hasDate) {
          const formattedDatePart = formatDateToDDMMYYYY(dateObj);
          return `${formattedDatePart}, ${formattedTimePart}`;
        }

        // Otherwise just show the time
        return formattedTimePart;
      }

      case "Boolean":
        return value !== null && value !== undefined ? value.toString() : "-";

      case "InLine":
        // Display hyphen for null, undefined, or empty string when not editing
        const displayValueInLine = (value == null || value === "") ? "-" : value;

        if (!permission?.CanEdit) return <div className="wrap-text">{displayValueInLine}</div>;
        const isCurrentlyEditing = editingCell.rowId === (item.ID) && editingCell.columnKey === col.Title;
        if (isCurrentlyEditing) {
          const originalValue = value;
          const inputType = typeof originalValue === 'number' ? 'number' : 'text';
          return (
            <Input
              type={inputType}
              bsSize="sm"
              value={editedValue}
              onChange={(e) => setEditedValue(e.target.value)}
              onBlur={() => {
                let valueToUpdate = editedValue;
                if (inputType === 'number') {
                  const parsedValue = parseFloat(editedValue);
                  valueToUpdate = isNaN(parsedValue) ? originalValue : parsedValue;
                }
                
                if (onInlineUpdate && editedValue !== (originalValue?.toString() ?? "")) {
                  onInlineUpdate(item, col.Title, valueToUpdate); 
                }
                setEditingCell({ rowId: null, columnKey: null });
              }}
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
            />
          );
      }

      case "Rating": 
        if (!value) return "-";
        return (
          <Rating
            initialRating={value}
            emptySymbol="mdi mdi-star-outline text-muted "
            fullSymbol="mdi mdi-star text-warning "
            readonly
          />
        );

      case "Dropdown":
        if (!onInlineUpdate) return <div className="wrap-text">{value ?? "-"}</div>; // If no update handler, just display

        const currentActualValue = item[col.Title]; 

        // Find display label for the current value when not editing
        const displayLabel = paymentStatusOptions.find(opt => opt.value === currentActualValue)?.label || currentActualValue || "-";        

        const isCurrentlyEditingDropdown = editingCell.rowId === (item.ID || item.Id) && editingCell.columnKey === col.Title;

        if (isCurrentlyEditingDropdown) {
          return (
            <Input
              type="select"
              bsSize="sm"
              value={editedValue} 
              onChange={(e) => {
                const newValue = e.target.value;
                if (onInlineUpdate) {
                  onInlineUpdate(item, col.Title, newValue); // Pass the whole item
                }
                setEditingCell({ rowId: null, columnKey: null }); // Exit editing mode
              }}
              onBlur={() => {
                setEditingCell({ rowId: null, columnKey: null }); // Exit editing mode if clicked away
              }}
              autoFocus
            >
              {paymentStatusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
          );
        }

        // Display mode: show current value and make it clickable to edit
        if (!permission?.CanEdit) {
          return <div className="wrap-text">{displayLabel}</div>;
        }

        return (
          <div className="wrap-text editable-cell" onClick={() => {
            if (permission?.CanEdit) {
              setEditingCell({ rowId: (item.ID || item.Id), columnKey: col.Title });
              setEditedValue(currentActualValue ?? ""); // Initialize with current value
            }
          }}>
            {displayLabel} {displayLabel !== "-" && <i className="ri-pencil-fill edit-icon ms-1"></i>}
          </div>
        );
     
      case "UserDropdown": 
      {
        const rowIdentifier = item.ID || item.Id;
        const currentUserId = value; 

        const selectedUser = users?.find(u => String(u.Id) == String(currentUserId));
        
        const displayUserName = selectedUser ? selectedUser.FullName : "-";

        if (!permission?.CanEdit) {
          return <div className="wrap-text">{displayUserName}</div>;
        }

        const isCurrentlyEditingUserDropdown = editingCell.rowId === rowIdentifier && editingCell.columnKey === col.Title;

        if (isCurrentlyEditingUserDropdown) {
          return (
            <Input
              type="select"
              bsSize="sm"
              value={editedValue} // This will be the UserId
              onChange={(e) => {
                const newUserId = e.target.value;
                onInlineUpdate(item, col.Title, newUserId);
                setEditingCell({ rowId: null, columnKey: null });
              }}
              onBlur={() => setEditingCell({ rowId: null, columnKey: null })}
              autoFocus
            >
              <option value="">{t("SelectUser", "Select User")}</option>
              {users?.map(user => (
                <option key={user.Id} value={user.Id}>
                  {user.FullName} ({user.Email})
                </option>
              ))}
            </Input>
          );
        } else { // Display mode for editable cell
          return (
            <div className="wrap-text editable-cell" onClick={() => {
              setEditingCell({ rowId: rowIdentifier, columnKey: col.Title });
              setEditedValue(currentUserId ?? ""); 
            }}>
              {displayUserName} {displayUserName !== "-" && <i className="ri-pencil-fill edit-icon ms-1"></i>}
            </div>
          );
        }
      }

      case "ReferralStatusDropdown": {
        const rowIdentifier = item.ID || item.Id;
        const currentStatusId = value;

        const selectedStatus = referralStatus?.find(s => String(s.ID) === String(currentStatusId));
        const displayStatusName = selectedStatus 
          ? (i18n.language === 'ar' ? selectedStatus.StatusAr : selectedStatus.StatusEn) 
          : "-";

        if (!permission?.CanEdit) {
          return <div className="wrap-text">{displayStatusName}</div>;
        }

        const isCurrentlyEditingStatusDropdown = editingCell.rowId === rowIdentifier && editingCell.columnKey === col.Title;

        if (isCurrentlyEditingStatusDropdown) {
          return (
            <Input
              type="select"
              bsSize="sm"
              value={editedValue} // This will be the ReferralStatusID
              onChange={(e) => {
                const newStatusId = e.target.value;
                onInlineUpdate(item, col.Title, newStatusId);
                setEditingCell({ rowId: null, columnKey: null });
              }}
              onBlur={() => setEditingCell({ rowId: null, columnKey: null })}
              autoFocus
            >
              <option value="">{t("SelectStatus", "Select Status")}</option>
              {referralStatus?.map(status => (
                <option key={status.ID} value={status.ID}>
                  {i18n.language === 'ar' ? status.StatusAr : status.StatusEn}
                </option>
              ))}
            </Input>
          );
        } else { // Display mode for editable cell
          return (
            <div className="wrap-text editable-cell" onClick={() => {
              setEditingCell({ rowId: rowIdentifier, columnKey: col.Title });
              setEditedValue(currentStatusId ?? ""); 
            }}>
              {displayStatusName} {displayStatusName !== "-" && <i className="ri-pencil-fill edit-icon ms-1"></i>}
            </div>
          );
        }
      }

      case "HoverDesc": {
      const desc = value || "-";
      const popoverId = `desc_popover_${col.Title}_${item.ID || item.Id}`;
      return (
        <>
          <a
            href="#!"
            onClick={e => e.preventDefault()}
            id={popoverId}
            className="d-inline-flex align-items-center"
            style={{ textDecoration: 'none', gap: '0.3rem' }}
          >
            <Badge pill color="info" className="text-dark">
              {t("Show")}
            </Badge>
          </a>
          <UncontrolledPopover
            placement="auto"
            target={popoverId}
            trigger="hover"
            delay={{ hide: 150 }}
          >
            <PopoverHeader>{t("Description")}</PopoverHeader>
            <PopoverBody style={{
              maxHeight: "180px",
              overflowY: "auto",
              minWidth: "220px",
              whiteSpace: "pre-wrap"
            }}>
              <span style={{ fontSize: '0.95rem' }}>{desc}</span>
            </PopoverBody>
          </UncontrolledPopover>
        </>
        );
      }

      case "Hover": {
        const desc = value || "-";
        const popoverId = `hover_${item.ID}`;
        return (
          <>
            <div className="wrap-text" id={popoverId}>
              {desc.length > 30 ? desc.substring(0, 30) + "..." : desc}
            </div>
            <UncontrolledPopover
              placement="auto"
              target={popoverId}
              trigger="hover"
              delay={{ hide: 150 }}
            >
              <PopoverHeader>{i18n.language === "ar" ? col.ValueAr : col.ValueEn}</PopoverHeader>
              <PopoverBody style={{
                maxHeight: "180px",
                overflowY: "auto",
                minWidth: "220px",
                whiteSpace: "pre-wrap"
              }}>
                <span style={{ fontSize: '0.95rem' }}>{desc}</span>
              </PopoverBody>
            </UncontrolledPopover>
          </>
          );
        }

      case "ChatStatusBadge": {
        // Map status values to colors and labels
        let badgeColor = "secondary";
        let badgeLabel = "-";
        switch (value) {
          case 1:
            badgeColor = "primary";
            badgeLabel = t("ChatReportLableNew");
            break;
          case 2:
            badgeColor = "info";
            badgeLabel = t("ChatReportLableInProgress");
            break;
          case 3:
            badgeColor = "warning";
            badgeLabel = t("ChatReportLableOnHold");
            break;
          case 4:
            badgeColor = "success";
            badgeLabel = t("ChatReportLableComplete");
            break;
          default:
            badgeColor = "secondary";
            badgeLabel = value ?? "-";
        }

        return <Badge color={badgeColor}>{badgeLabel}</Badge>;
      }

      case "ChatHistoryModal": {
          const messages = parseInt(value);

          return (
            <Button
              // color="light"
              size="sm"
              onClick={() => {
                if (onViewHistory) {
                  onViewHistory(item); // Pass the entire session object
                }
              }}
              // disabled={!messages} 
              className="w-100"
            >
              {/* Show the count of messages as seen in your screenshot */}
              {t("View")} {messages ? `(${messages})` : ""}
            </Button>
          );
      }

      case "TransationBtn": {
        // If no click handler is provided, display a muted "-"
        if (!handleButtonClick) return <span className="text-muted">-</span>;
        return (
          <Button
              type="button"
              color="soft-secondary" // Using reactstrap color prop
              size="sm"
              onClick={() => handleButtonClick(item, col.Title)}
          >
              <i className="ri-exchange-dollar-line me-1"></i> {t("Transactions")}
          </Button>
        );
      }


      default:
        return <div className="wrap-text">{value ?? "-"}</div>; 
    }
  };


  const CommonDataTable = ({
    data = [], 
    tableColumnNames = [],
    searchTerm = "",
    onEditClickHandler,
    onActiveOrDeactiveChange,
    setDeleteModal, 
    setSelectedIds, 
    handleCheckbox,
    handleNavigate, 
    handleCancel,
    onInlineUpdate,
    handleButtonClick,
    actionConfig = {}, 
    permission = {},
    onApplyClick,
    onViewHistory,
    onRowClick,
    onPageChange,
    disabled = false,
    totalRecords,
    recordsPerPage = 100
  }) => {
    const { t, i18n } = useTranslation();
    const { user } = getAuthUser();

    // Internal state for selection within the table page
    const [selectedRowKeys, setSelectedRowKeys] = useState([]); 
    const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(recordsPerPage);

    // State for inline editing
    const [editingCell, setEditingCell] = useState({ rowId: null, columnKey: null });
    const [editedValue, setEditedValue] = useState("");

    const { users = [], referralStatus = [] } = useSelector((state) => ({ // Ensure users defaults to an array
      users: state.Common.users,
      referralStatus: state.ReferralEarn.referralStatus, 
    }));

    const NoSortField = [
      "Active",
      "List"
    ]

    // Memoize filtered columns based on permissions and user settings
    const visibleColumns = useMemo(() => {
      let columns = tableColumnNames; 
      
      
      const userSpecificColumns = tableColumnNames.filter(col => col?.UserID?.includes(parseInt(user.ID)));
      
      if (userSpecificColumns.length > 0) {
          columns = userSpecificColumns;
      } else {
          columns = tableColumnNames.filter(col => col?.RoleID?.includes(user.RoleID));
      }

      if (!permission?.CanActive) {
        columns = columns.filter((col) => col.FieldName !== "Active");
      }
      return columns.filter(col => col.IsVisible !== false);
    }, [tableColumnNames, user?.ID, user?.RoleID, permission?.CanActive]);


    // Memoize sorted and filtered data
    const processedData = useMemo(() => {
      let filteredData = [...data]; 

      if (searchTerm) {
        const lowerSearchTerm = searchTerm.trim().toLowerCase();
        filteredData = filteredData.filter((item) =>
          tableColumnNames.some((col) => {
            const value = item[col.Title];
            if (value && typeof value === "object") { 
              return Object.values(value).some((nestedVal) =>
                nestedVal?.toString().toLowerCase().includes(lowerSearchTerm)
              );
            }
            return value?.toString().toLowerCase().includes(lowerSearchTerm);
          })
        );
      }

      if (sortConfig.key) {
        const { key: sortKey, direction } = sortConfig;

        // Find the column definition for the current sort key
        const sortColumn = visibleColumns.find(col => col.Title === sortKey);

        const getValue = (val) => {
          if (val && typeof val === 'object' && !Array.isArray(val) && !(val instanceof Date)) {
            return val[i18n.language] ?? undefined;
          }
          return val;
        };

        const compareValues = (a, b) => {
          const aVal = getValue(a[sortKey]);
          const bVal = getValue(b[sortKey]);

          // Handle null/undefined specifically to sort them together
          const aIsNull = aVal === null || aVal === undefined;
          const bIsNull = bVal === null || bVal === undefined;

          if (aIsNull && bIsNull) return 0; 
          if (aIsNull) return 1;  // a is null/undefined, b is not; sort a after b (for asc)
          if (bIsNull) return -1; // b is null/undefined, a is not; sort a before b (for asc)

          // At this point, neither aVal nor bVal is null or undefined.

          // Date comparison
          if (sortColumn && ['Date', 'DateTime', 'Time'].includes(sortColumn.FieldName)) {
            const dateA = new Date(aVal);
            const dateB = new Date(bVal);
            const aIsValidDate = !isNaN(dateA.valueOf());
            const bIsValidDate = !isNaN(dateB.valueOf());
            
            if (aIsValidDate && bIsValidDate) return dateA - dateB;
            // If one is valid and the other isn't, the valid one should come first.
            if (aIsValidDate) return -1;
            if (bIsValidDate) return 1;
            // If both are invalid date strings (e.g. non-date strings in a date column), fall through.
          }

          // Numeric comparison
          const numA = parseFloat(aVal);
          const numB = parseFloat(bVal);
          // Only compare as numbers if both are parsable as finite numbers.
          if (!isNaN(numA) && isFinite(numA) && !isNaN(numB) && isFinite(numB)) {
            return numA - numB;
          }

          // Boolean comparison
          if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
            return aVal === bVal ? 0 : aVal ? 1 : -1; // true sorts after false
          }

          // Fallback: String comparison for all other cases (including empty strings, " - ", actual text)
          // Values are guaranteed not to be null or undefined here.
          const strA = String(aVal).toLowerCase();
          const strB = String(bVal).toLowerCase();

          return strA.localeCompare(strB);
        };

        filteredData.sort((a, b) => {
          const result = compareValues(a, b);
          return direction === 'asc' ? result : -result;
        });
      }
      return filteredData;
    }, [data, searchTerm, sortConfig, visibleColumns, i18n.language]); 


    // Memoize paginated data
    const paginatedData = useMemo(() => {
      return processedData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      );
    }, [processedData, currentPage, itemsPerPage]);

    
    // Reset page to 1 if filtered data results in fewer pages
    useEffect(() => {
      if (processedData.length > 0 && Math.ceil(processedData.length / itemsPerPage) < currentPage) {
        setCurrentPage(1);
      }
    }, [processedData.length, itemsPerPage]);


    const handleSort = (key) => {
      setSortConfig((prev) => ({
        key,
        direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
      }));
    };

    const getItemKey = (item) => String(item?.Id ?? item?.ID);
    
    const handleRowSelect = (rowItem) => {
      const key = getItemKey(rowItem);

      const newSelectedRowKeys = selectedRowKeys.includes(key)
        ? selectedRowKeys.filter((k) => k !== key)
        : [...selectedRowKeys, key];

      setSelectedRowKeys(newSelectedRowKeys);
      if (setSelectedIds) {
        setSelectedIds(newSelectedRowKeys);
      }
    };


    const handleSelectAll = (e) => {
      let newSelectedRowKeys = [];

      if (e.target.checked) {
        newSelectedRowKeys = paginatedData.map((item) => getItemKey(item));
      }

      setSelectedRowKeys(newSelectedRowKeys);
      if (setSelectedIds) {
        setSelectedIds(newSelectedRowKeys);
      }
    };


    const handlePageChange = (pages, items) => {
      if (currentPage == pages && itemsPerPage == items) return;
      if (onPageChange && (currentPage < pages || itemsPerPage != items)) {
        onPageChange(pages, items, setCurrentPage);
      } else {
        setCurrentPage(pages);
      }
    }

    const handleDeleteClick = useCallback((id) => {
      if (setSelectedIds) {
        setSelectedIds([id]);
      }
      if (setDeleteModal) {
        setDeleteModal(true);
      }
    }, [setSelectedIds, setDeleteModal]);

    // Cell rendering handlers and state grouped for passing to renderCellContent
    const cellHandlers = useMemo(() => ({
      onActiveOrDeactiveChange,
      handleCheckbox,
      handleButtonClick,
      onInlineUpdate,
      permission,
      onViewHistory,
      onRowClick
    }), [onActiveOrDeactiveChange, handleCheckbox, handleButtonClick, onInlineUpdate, permission, onViewHistory, onRowClick ]);

    const cellEditState = useMemo(() => ({
      editingCell, editedValue, setEditingCell, setEditedValue
    }), [editingCell, editedValue]);


    if (!data || data.length === 0 && !searchTerm) {
      return (
          <div className="noresult text-center py-4">
              <h5 className="mt-2">{t("NoDataToDisplay")}</h5>
          </div>
      );
    }
    
    return (
      <React.Fragment>
        <div className="table-responsive table-card mb-1">
          {paginatedData.length > 0 ? (
            <table className="table align-middle table-nowrap table-hover">
              <thead className="table-light">
                <tr>
                  {actionConfig?.showCheckboxColumn !== false && (
                    <th scope="col" style={{ width: "50px" }}>
                      <div className="form-check">
                       <Input
                          className="form-check-input"
                          type="checkbox"
                          id="selectAllCheckbox"
                          checked={
                            paginatedData.length > 0 &&
                            paginatedData.every((item) =>
                              selectedRowKeys.includes(getItemKey(item))
                            )
                          }
                          onChange={handleSelectAll}
                          disabled={paginatedData.length === 0}
                        />
                      </div>
                    </th>
                  )}


                  {visibleColumns.map((col, i) => {
                    return (
                    <th
                      key={col.ColumnId || col.Title}
                      onClick={() => !NoSortField.includes(col.FieldName) && handleSort(col.Title)} // No sort on fields in NoSortField
                      style={{ cursor: !NoSortField.includes(col.FieldName) ? "pointer" : "default" }}
                    >
                      {i18n.language === "ar" ? col.ValueAr : col.ValueEn}{" "}
                      {!NoSortField.includes(col.FieldName) && sortConfig.key === col.Title && (
                        sortConfig.direction === "asc" ? "▲" : "▼"
                      )}
                      {!NoSortField.includes(col.FieldName) && sortConfig.key !== col.Title && (
                        <i className="bx bxs-sort-alt"></i>
                      )}
                    </th>
                    )
                  })}
                
                  {actionConfig?.action !== false && <th style={{ width: "160px" }}>{t("Action")}</th>}
                </tr>
              </thead>

              <tbody>
                {paginatedData.map((item) => ( 
                  <tr key={item.Id ? item.Id : item.ID} className={
                    // item.Status == 4 ? 'text-dark text-bg-success bg-opacity-50' : 
                    [12, 13].includes(item?.Status) ? 'text-dark text-bg-danger bg-opacity-50' : 
                    ''}>
                    {actionConfig?.showCheckboxColumn !== false && (
                      <th scope="row">
                        <div className="form-check">
                          <Input // Use Reactstrap Input
                            className="form-check-input"
                            type="checkbox"
                            id={`rowCheckbox-${item.Id ? item.Id : item.ID}`}
                            checked={selectedRowKeys.includes(getItemKey(item))}
                            onChange={() => handleRowSelect(item)}
                          />
                        </div>
                      </th>
                    )}

                    {visibleColumns.map((col, i) => (
                      <td className={item.IsDelete ? "text-danger text-decoration-line-through" : ""} key={i || col.ColumnId || col.Title}>
                        {renderCellContent(item, col, t, i18n, cellHandlers, cellEditState, users, referralStatus, disabled)}
                      </td>
                    ))}

                    {/* Actions  */}

                    {actionConfig?.action !== false && (                      
                      <td style={{ minWidth: "160px" }}>
                        <div className="hstack gap-3 flex-wrap justify-content-center" >
                          {Object.entries(actionConfig).map(([actionKey, actionProperties]) => {
                            // Handle visibility
                            const isVisible = typeof actionProperties.isVisible === "function"
                              ? actionProperties.isVisible(item, permission)
                              : true; 

                            if (!isVisible) return null;                            

                            // Prefer onClick from actionProperties if available
                            if (typeof actionProperties.onClick === 'function') {
                              return (
                                <Link
                                  key={actionKey}
                                  to="#"
                                  className={`fs-15 ${
                                    actionProperties.className || 
                                    (actionKey === 'view' ? 'link-info' : 
                                     actionKey === 'edit' ? 'link-success' :
                                     actionKey === 'delete' ? 'link-danger' :
                                     'link-primary') 
                                  }`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    actionProperties.onClick(item);
                                  }}
                                  title={actionProperties.tooltip ? t(actionProperties.tooltip) : (actionKey.charAt(0).toUpperCase() + actionKey.slice(1))}
                                >
                                  <i className={actionProperties.icon}></i>
                                </Link>
                              );
                            }

                            // Fallback for actions that rely on specific props passed to CommonDataTable
                            switch (actionKey) {                              
                              case "cancel":
                                if (handleCancel) { 
                                  return (
                                    <Link
                                      key={actionKey}
                                      to="#"
                                      className="link-danger fs-15 mt-1"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleCancel(item); 
                                      }}
                                      title={t(actionProperties.tooltip)}
                                    >
                                      <i className={actionProperties.icon }></i>
                                    </Link>
                                  );
                                }
                                return null;

                              case "apply":
                                if (onApplyClick) {
                                  return (
                                    <Link
                                      key={actionKey}
                                      to="#"
                                      className="link-primary fs-15" 
                                      onClick={(e) => {
                                        e.preventDefault();
                                        onApplyClick(item);
                                      }}
                                      title={actionProperties.tooltip}
                                    >
                                      <i className={actionProperties.icon}></i>
                                    </Link>
                                  );
                                }
                                return null;
                                
                              case "edit":
                                if (permission?.CanEdit && onEditClickHandler && !actionProperties.onClick) { // Only if not handled by actionProperties.onClick
                                  return (
                                    <Link
                                      key={actionKey}
                                      to="#"
                                      className="link-success fs-15"
                                      onClick={(e) => {
                                        e.preventDefault(); // Prevent default link behavior
                                        onEditClickHandler(item, "isEdit");
                                      }}
                                      title={t(actionProperties.tooltip) || t("Edit")}
                                    >
                                      <i className={actionProperties.icon || "ri-edit-2-line"}></i>
                                    </Link>
                                  );
                                }
                                return null;

                              case "delete":
                                if (permission?.CanDelete && setDeleteModal && !actionProperties.onClick) { // Only if not handled by actionProperties.onClick
                                  return (
                                    <Link
                                      key={actionKey}
                                      to="#"
                                      className="link-danger fs-15"
                                      onClick={() => handleDeleteClick(item.Id ? item.Id : item.ID)}
                                      title={actionProperties.tooltip || t("Delete")}
                                    >
                                      <i className={actionProperties.icon || "ri-delete-bin-2-line"}></i>
                                    </Link>
                                  );
                                }
                                return null;

                              case "permission":
                                if (handleNavigate && !actionProperties.onClick) { // Only if not handled by actionProperties.onClick
                                  return (
                                    <Button
                                      key={actionKey}
                                      color="link"
                                      style={{ padding: 0 }}
                                      className="link-primary fs-15" 
                                      onClick={() => handleNavigate(item.ID)}
                                      title={actionProperties.tooltip || t("Permission")}
                                    >
                                      <i className={actionProperties.icon || "ri-lock-unlock-line"}></i>
                                    </Button>
                                  );
                                }
                                return null;

                              default:
                                return null; // Fallback for any other action keys in actionConfig
                            }
                          })}
                        </div>
                      </td>
                    )}

                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="noresult text-center py-4">
              {searchTerm ? <p>{`${t("YourSearchReturnedNoResults")} : " ${searchTerm} "`}</p> :
                <h5 className="mt-2">{t("NoDataToDisplay")}</h5>}
            </div>
          )}
          
          <PaginationControls
            totalEntries={totalRecords || processedData.length}
            rowsPerPage={itemsPerPage}
            setRowsPerPage={setItemsPerPage}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            handlePageChange={handlePageChange}
          />

        </div>
      </React.Fragment>
    );
  };

  /*
  // --- BasicTable.js (Example of moving it to its own file) ---
  import React from 'react';
  import DataTable from 'react-data-table-component';
  import { useTranslation } from 'react-i18next';

  export const BasicTable = ({ title, data, columns }) => {
    const { t } = useTranslation();

    return (
      <DataTable
        title={title}
        columns={columns}
        data={data}
        responsive
              pagination // Simplified pagination prop
        paginationPerPage={50} // Default items per page
        paginationRowsPerPageOptions={[50, 100, 200]}
        highlightOnHover
        striped
        noDataComponent={
          <div style={{ padding: '24px', textAlign: 'center' }}>
            {t('NoData')}
          </div>
        }
        paginationComponentOptions={{
          rowsPerPageText: t('RowsPerPage'),
          rangeSeparatorText: t('of'),
          selectAllRowsItem: false, // Usually false if you handle selection externally or don't need it
          selectAllRowsItemText: t('All'),
        }}
      />
    );
  };
  // --- End of BasicTable.js ---
  */


export default React.memo(CommonDataTable);